"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { components } from "@/lib/api.gen.d";
import { getUserImageSrc, getUserInitials, userIconColorValues } from "@/lib/user-icon";
import Link from "@/components/link";
import { useUserStats } from "@/lib/api/user";
import { UserStatsSummary } from "@/components/user-stats-summary";
import { useTranslations } from "next-intl";

/**
 * Base user plus the optional owner fields that only event participants
 * (`EventUser`) carry. Callers may pass a plain `User`; the owner fields are
 * then simply absent.
 */
type User = components["schemas"]["User"] & {
  super_user_id?: string | null;
  super_user_name?: string | null;
};
interface UserIconProps {
  user: User;
  size?: number;
  showInfo?: boolean;
  /**
   * How the info popover is opened. `"hover"` (default) opens on mouse enter;
   * `"click"` opens on click. In both cases the Popover is only mounted after
   * the first interaction, keeping the DOM light for long lists of icons.
   */
  trigger?: "hover" | "click";
}
export default function UserIcon(t0) {
  const $ = _c(51);
  const {
    user,
    size: t1,
    showInfo: t2,
    trigger: t3
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  const showInfo = t2 === undefined ? true : t2;
  const trigger = t3 === undefined ? "hover" : t3;
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [activated, setActivated] = useState(false);
  const {
    data: stats
  } = useUserStats(user.id, activated);
  const bgColor = userIconColorValues[user.color] ?? userIconColorValues.green;
  let t4;
  if ($[0] !== user.profile_picture) {
    t4 = getUserImageSrc(user.profile_picture);
    $[0] = user.profile_picture;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const imageSrc = t4;
  const hasInfo = !!user.name || !!user.description;
  let t5;
  if ($[2] !== size) {
    t5 = {
      width: size,
      height: size
    };
    $[2] = size;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== imageSrc || $[5] !== user.name) {
    t6 = imageSrc && <AvatarImage src={imageSrc} alt={user.name} className="object-cover" />;
    $[4] = imageSrc;
    $[5] = user.name;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== bgColor) {
    t7 = {
      backgroundColor: bgColor,
      color: "black",
      fontWeight: 600
    };
    $[7] = bgColor;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== user.name) {
    t8 = getUserInitials(user.name);
    $[9] = user.name;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <AvatarFallback style={t7}>{t8}</AvatarFallback>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t9) {
    t10 = <Avatar className="border-2 border-white" style={t5}>{t6}{t9}</Avatar>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const avatar = t10;
  if (!showInfo || !hasInfo) {
    return avatar;
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setActivated(true);
      setOpen(true);
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const activate = t11;
  if (!activated) {
    const t12 = trigger === "click" ? "cursor-pointer" : undefined;
    const t13 = trigger === "hover" ? activate : undefined;
    const t14 = trigger === "click" ? activate : undefined;
    let t15;
    if ($[19] !== avatar || $[20] !== t12 || $[21] !== t13 || $[22] !== t14) {
      t15 = <span className={t12} onMouseEnter={t13} onClick={t14}>{avatar}</span>;
      $[19] = avatar;
      $[20] = t12;
      $[21] = t13;
      $[22] = t14;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    return t15;
  }
  let t12;
  if ($[24] !== trigger) {
    t12 = trigger === "hover" ? () => setOpen(true) : undefined;
    $[24] = trigger;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== avatar || $[27] !== t12) {
    t13 = <PopoverTrigger asChild={true} onMouseEnter={t12}>{avatar}</PopoverTrigger>;
    $[26] = avatar;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setOpen(true);
    t15 = () => setOpen(false);
    $[29] = t14;
    $[30] = t15;
  } else {
    t14 = $[29];
    t15 = $[30];
  }
  const t16 = `/profile/${user.id}`;
  let t17;
  if ($[31] !== t16 || $[32] !== user.name) {
    t17 = <Link href={t16} className="text-sm font-semibold leading-tight hover:underline">{user.name}</Link>;
    $[31] = t16;
    $[32] = user.name;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== user.description) {
    t18 = user.description ? <p className="text-xs leading-tight text-muted-foreground">{user.description}</p> : null;
    $[34] = user.description;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t || $[37] !== user.super_user_id || $[38] !== user.super_user_name) {
    t19 = user.super_user_id && user.super_user_name ? <p className="text-xs leading-tight text-muted-foreground">{t("user.belongs-to")}:{" "}<Link href={`/profile/${user.super_user_id}`} className="font-medium text-foreground hover:underline">{user.super_user_name}</Link></p> : null;
    $[36] = t;
    $[37] = user.super_user_id;
    $[38] = user.super_user_name;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== stats) {
    t20 = stats ? <UserStatsSummary seconds={stats.total_helper_seconds} taskCount={stats.task_count} variant="inline" className="pt-1" /> : null;
    $[40] = stats;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t21 = <PopoverContent side="bottom" align="center" className="max-w-72" onMouseEnter={t14} onMouseLeave={t15}><div className="space-y-1">{t17}{t18}{t19}{t20}</div></PopoverContent>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== open || $[48] !== t13 || $[49] !== t21) {
    t22 = <Popover open={open} onOpenChange={setOpen}>{t13}{t21}</Popover>;
    $[47] = open;
    $[48] = t13;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  return t22;
}
export { getUserImageSrc };